<template>
  <a-drawer
    :open="visible"
    title="Add SSH Key"
    width="520px"
    :body-style="{ padding: '0' }"
    :footer-style="{ padding: '0' }"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-6 p-4">
      <div id="key_name" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Key Name</span>
        <a-input
          v-model:value="formData.name"
          placeholder="Enter key name"
          class="input_medium"
        />
      </div>

      <div id="key_type" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Key Type</span>
        <a-select
          v-model:value="formData.keyType"
          placeholder="Select key type"
        >
          <a-select-option
            v-for="type in sshKeyTypes"
            :key="type.value"
            :value="type.value"
          >
            {{ type.label }}
          </a-select-option>
        </a-select>
      </div>

      <div id="public_key" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Public Key</span>
        <a-textarea
          v-model:value="formData.publicKey"
          placeholder="Paste your public key here (e.g. ssh-ed25519 AAAA...)"
          :rows="10"
          class="input_medium"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2 px-4 py-3">
        <a-button class="secondary_medium" @click="emit('close')">
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          :loading="adding"
          :disabled="!canAdd"
          @click="handleAdd"
        >
          {{ adding ? "Adding..." : "Add SSH Key" }}
        </a-button>
      </div>
    </template>
  </a-drawer>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { sshKeyTypes } from "@/src/constants/codebaseConstants.js";
import codebaseService from "@/src/api/CodeService.js";

const props = defineProps({
  visible: { type: Boolean, required: true },
});

const emit = defineEmits(["close", "created"]);

const formData = reactive({ name: "", keyType: null, publicKey: "" });
const adding = ref(false);

const canAdd = computed(
  () => formData.name?.trim() && formData.keyType && formData.publicKey?.trim(),
);

const resetForm = () => {
  formData.name = "";
  formData.keyType = null;
  formData.publicKey = "";
};

const handleAdd = async () => {
  try {
    adding.value = true;
    const payload = {
      name: formData.name.trim(),
      key_type: formData.keyType,
      public_key: formData.publicKey.trim(),
    };
    const response = await codebaseService.base.sshKeys.create(payload);
    if (response?.success) {
      message.success("SSH key added successfully");
      emit("created");
      emit("close");
    }
  } catch (error) {
    console.error("An error occurred while adding SSH key:", error);
    message.error(error?.response?.data?.error || "Failed to add SSH key");
  } finally {
    adding.value = false;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetForm();
    }
  },
);
</script>
